import { RcFile } from 'antd/es/upload';
import { parsePhoneNumber } from 'libphonenumber-js';
import { NavigateFunction } from 'react-router-dom';
import { messageContext } from '../components/AppContextHolder';
import { BREAK_POINTS, MOBILE_SIZE, REGEX, TABLET_SIZE } from './constants';

export const formValidatorRules = {
  required: {
    required: true,
    message: 'Required',
  },
  email: () => ({
    validator(_rule: unknown, value?: string) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX.EMAIL?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('The input is not valid E-mail!');
      }
      return Promise?.resolve();
    },
  }),
  otp: () => ({
    validator(_rule: unknown, value?: string) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX.NUMBER?.test(value)) {
        return Promise?.reject('OTP should only contain numbers!');
      }
      return Promise?.resolve();
    },
  }),
  name: () => ({
    validator(_rule: unknown, value?: string) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX.NAME?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Please enter valid name');
      }
      return Promise?.resolve();
    },
  }),
};

export const getBase64 = (file: RcFile) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader?.result);
    reader.onerror = (error) => reject(error);
  });

export const getTimeFromMins = (mins: number) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${hours}h ${minutes}m`;
};

export const getBase64File = (
  img: Blob,
  // eslint-disable-next-line no-unused-vars
  callback: (result: string | ArrayBuffer | null) => void,
) => {
  // eslint-disable-next-line no-undef
  const reader = new FileReader();
  reader?.addEventListener('load', () => callback(reader?.result));
  reader?.readAsDataURL(img);
};

export const beforeUpload = (file: RcFile) => {
  const isJpgOrPng =
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/jpg';
  if (!isJpgOrPng) {
    messageContext.error('You can only upload JPG/PNG/JPEG file!');
  }
  const isLt2M = file?.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    messageContext.error('Image must smaller than 25MB!');
  }
  return isJpgOrPng && isLt2M;
};

export async function fileUpload(signedUrl: string | URL, image: File) {
  try {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr?.open('PUT', signedUrl);
      xhr?.setRequestHeader('Content-Type', image?.type);
      xhr?.addEventListener('readystatechange', () => {
        if (xhr?.status === 200) {
          resolve(xhr?.response);
        } else {
          reject(new Error('Upload failed!'));
        }
      });
      xhr?.send(image);
    });
  } catch (error) {
    messageContext?.error('error');
  }
}

export const handleProtectedNavigation = (
  allow: boolean,
  callback: NavigateFunction,
  path: number,
) => (allow ? callback(path) : false);

// Portal related methods
export const injectUsingPortal = (portalId: string) =>
  // eslint-disable-next-line no-undef
  document.getElementById(portalId);

export const isPortalIdExists = (portalId: string) =>
  !!injectUsingPortal(portalId);

// create cookie
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setCookie = (
  name: string,
  value: string | number | boolean,
  minutes: number,
) => {
  let expires = '';
  if (minutes) {
    const date = new Date();
    date?.setTime(date?.getTime() + minutes * 60 * 1000);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    expires = `; expires=${(date as any)?.toGMTString()}`; //as using toGMTString
  }
  // eslint-disable-next-line no-undef
  document.cookie = `${name}=${value}${expires}; path=/`;
};

// retrieve cookie
export const getCookie = (cookieName: string) => {
  const name = `${cookieName}=`;
  let returnCookie = '';
  // eslint-disable-next-line no-undef
  const decodedCookie = decodeURIComponent(document?.cookie);
  const cookieArray = decodedCookie?.split(';');
  if (cookieArray?.length > 0) {
    // eslint-disable-next-line array-callback-return
    cookieArray?.map((item) => {
      let cookie = item;
      while (cookie?.charAt(0) === ' ') {
        cookie = cookie?.substring(1);
      }
      if (cookie?.indexOf(name) === 0) {
        returnCookie = cookie?.substring(name?.length, cookie?.length);
      }
    });
    return returnCookie;
  }
};

// force expired cookie or delete
export const deleteCookie = (name: string) => {
  // eslint-disable-next-line no-undef
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

export const formItemProps = { normalize: (value: string) => value?.trim() };

export const getNumberAndCode = (number: string) => {
  const phoneNumber = `+${number?.replace(/\D/g, '')}`;
  const isNewNumber = parsePhoneNumber(phoneNumber);
  return isNewNumber;
};

export const isMobileBrowser = (width: number) => {
  if (width <= MOBILE_SIZE) {
    return true;
  } else false;
};

export const isTabletBrowser = (width: number) => {
  if (width <= TABLET_SIZE) {
    return true;
  } else false;
};

export const detectDeviceType = (width: number): string => {
  if (width <= BREAK_POINTS.screen320) return 'extra-small';
  if (width <= BREAK_POINTS.screen375) return 'small';
  if (width <= BREAK_POINTS.screen425) return 'small-plus';
  if (width <= BREAK_POINTS.screen540) return 'medium';
  if (width <= BREAK_POINTS.screen767) return 'tablet-small';
  if (width <= BREAK_POINTS.screen991) return 'tablet';
  if (width <= BREAK_POINTS.screen1024) return 'tablet-large';
  if (width <= BREAK_POINTS.screen1150) return 'desktop-small';
  if (width <= BREAK_POINTS.screen1450) return 'desktop-medium';
  return 'desktop-large';
};

export const dateFormat = (date: string | Date | number): string => {
  if (!date) return '';

  const parsedDate = new Date(date);
  if (isNaN(parsedDate?.getTime())) return 'Invalid Date';

  const day = parsedDate?.getDate()?.toString()?.padStart(2, '0');
  const month = (parsedDate?.getMonth() + 1)?.toString()?.padStart(2, '0');
  const year = parsedDate?.getFullYear();

  return `${day}/${month}/${year}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setItem = (name: string, value: any) => {
  // eslint-disable-next-line no-undef
  localStorage?.setItem(name, value);
};

export const getItem = (name: string) => {
  // eslint-disable-next-line no-undef
  const value = localStorage?.getItem(name);
  return value;
};

export const removeItem = (name: string) => {
  // eslint-disable-next-line no-undef
  localStorage.removeItem(name);
};
